import { Calendar, LocaleInput } from '@fullcalendar/core'
import dayGridPlugin from '@fullcalendar/daygrid'
import bootstrapPlugin from '@fullcalendar/bootstrap'

import EventShowURL from "tm:~endpoint:{controller: 'event', action: 'show', event_id: ':id'}"
import { onCartLoaded } from "@frontend/cart"
import { allTranslationsLoded, getGlobalLocale, $_ } from '@frontend/translations';
import { EventOptions, Shop } from '@ticketmachine/sdk';
import { replaceURLParams } from '../../../utils/http';
import { MultiQueue, promiseDelegate } from '../../../utils/promises';

import "./stylesheets/calendar/core/main.min.css";
import "./stylesheets/calendar/daygrid/main.min.css";
import "./stylesheets/calendar/timegrid/main.min.css";
import "./stylesheets/calendar/list/main.min.css";
import "./stylesheets/calendar/theme.css.less";

function getCaledarLocale(): LocaleInput {
    return {
        code: getGlobalLocale(),
        week: {
          dow: Number($_("calendar.config.first-day-of-week")), // Monday is the first day of the week.
          doy: Number($_("calendar.config.safe-day-of-year-containing-first-week")), // The week that contains Jan 4th is the first week of the year.
        },
        buttonText: {
          prev: $_("calendar.button.prev.text"),
          next: $_("calendar.button.next.text"),
          today: $_("calendar.button.today.text"),
          year: $_("calendar.button.year.text"),
          month: $_("calendar.button.month.text"),
          week: $_("calendar.button.week.text"),
          day: $_("calendar.button.day.text"),
          list: $_("calendar.button.list.text"),
        },
        weekText: $_("calendar.week.short"),
        weekTextLong: $_("calendar.week.long"),
        allDayText: $_("calendar.allDay"),
        moreLinkText(n) {
          return $_("calendar.more.text", [n], ["count"]);
        },
        noEventsText: $_("calendar.events", [0], ["count"]),
        buttonHints: {
          prev: (text: string, name: string) => $_("calendar.prev.hint", [text, name], ["text", "name"]),
          next: (text: string, name: string) => $_("calendar.next.hint", [text, name], ["text", "name"]),
          today: (text: string, name: string) => $_("calendar.today.hint", [text, name], ["text", "name"]),
        },
        viewHint(buttonText) {
          return $_("calendar.view.hint", [buttonText], ["buttonText"]);
        },
        navLinkHint: (_, date) => $_("calendar.navlink.hint", [date], ["date"]),
        moreLinkHint(eventCnt: number) {
          return $_("calendar.more.hint", [eventCnt], ["count"]);
        },
        closeHint: $_("calendar.close"),
        timeHint: $_("calendar.time"),
        eventHint: $_("calendar.event")
    } as LocaleInput
}

export default async function calendar() {
    const calendarSource = document.getElementById("calendar")!;
    console.log("Loaded plugins:", bootstrapPlugin, dayGridPlugin);

    const q = new URLSearchParams(window.location.search);
    const baseOptions: Partial<EventOptions> = {};
    if (q.has("q") && !!q.get("q")) baseOptions.search = q.get("q")!;
    if (q.has("tag") && !!q.get("tag")) baseOptions.tag = q.get("tag")!;

    const colors = {
        loading: "var(--alert-info-background-color)",
        available: "var(--alert-success-background-color)",
        full: "var(--alert-error-background-color)",
        text: "var(--tm-calendar-text-color)"
    };

    async function collectAll<T>(gen: AsyncGenerator<T>): Promise<T[]> {
        const ary = [];
        for await (let it of gen) ary.push(it);
        return ary;
    }
    
    allTranslationsLoded(() => {
        const locale = getCaledarLocale();
        onCartLoaded(() => (async () => {
            const firstEvent = await collectAll(Shop.current.listEvents({from: new Date(), page: 1, page_size: 1, ...baseOptions}));
            const firstDate = firstEvent.length == 0 ? new Date() : firstEvent[0]!.startDate;
            const calendar = new Calendar(calendarSource, {
              locale: locale,
              plugins: [dayGridPlugin, bootstrapPlugin],
              themeSystem: "bootstrap",
              initialView: 'dayGridMonth',
              initialDate: firstDate,
              height: "auto",
              headerToolbar: false,
              weekNumbers: false,
              views: {
                  dayGridMonth: {}
              },
              eventSources: [
                  ({start, end}, rs, rj) => (async () => {
                      type Event = {id: string, start: Date, end: Date, url: string, title: string, backgroundColor: string, textColor: string, borderColor: string, color: string};
                      const allEvents = await collectAll(Shop.current.listEvents({from: start, to: end, ...baseOptions}));
                      if (allEvents.length === 0) return [];

                      const [isDone, setDone, _] = promiseDelegate<void>();
                      const queue = new MultiQueue(4);

                      const result: Event[] = [];
                      const resultCache: Record<string, Event> = {};
                      let left = allEvents.length;

                      for (let event of allEvents) {
                          const entry: Event = {
                              id: "e"+event.id,
                              title: event.name,
                              start: event.startDate,
                              end: event.endDate,
                              url: replaceURLParams(EventShowURL, {id: event.id}),
                              textColor: colors.text,
                              color: colors.loading,
                              backgroundColor: colors.loading,
                              borderColor: colors.loading
                          };
                          resultCache[entry.id] = entry;
                          result.push(entry);
                          queue.enqueue(((e) => async () => {
                              const allCategories = await Promise.all((await e.getPriceData()).categories.map(c => c.contingent()));
                              const contingent = allCategories.reduce((p, n) => p+n, 0);
                              entry.backgroundColor = contingent == 0 ? colors.full : colors.available;
                              entry.borderColor = contingent == 0 ? colors.full : colors.available;
                              entry.color = contingent == 0 ? colors.full : colors.available;

                              left--;
                              if (left == 0) {
                                  setDone();
                              }
                          })(event));
                      }

                      await isDone;
                      queue.close();
                      return result;
                  })().then(rs, rj)
              ]
            });
            calendar.render();

            // Display the current month.
            const title = document.getElementById("calendar-title")!;
            title.innerText = $_("calendar.title", [firstDate], ["date"]);
            calendar.on("datesSet", () => {
                title.innerText = $_("calendar.title", [calendar.getDate()], ["date"]);
            });

            // Make next and prev work.
            document.getElementById("calendar-prev")!.addEventListener("click", () => {
                calendar.prev();
            });
            document.getElementById("calendar-next")!.addEventListener("click", () => {
                calendar.next();
            });
        })().catch(window.reportError));
    });
}
